import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * InlineHelpBody — renders the body of an inline-help topic.
 *
 * v1 contract: the DP API returns `body_html` pre-compiled at sync time
 * (`sync-inline-docs` invokes the Markdoc compiler with a fixed safelist
 * of slot components). The FE just paints the HTML inside a prose wrapper
 * that styles the safelist output to match the rest of the DS.
 *
 * Slot components emit specific class names so our prose CSS can target
 * them without parsing the AST again:
 *   <div class="ia-callout ia-callout-info">  …  </div>     {% callout type="info" %}
 *   <div class="ia-callout ia-callout-warn">  …  </div>     {% callout type="warn" %}
 *   <div class="ia-callout ia-callout-danger">…</div>       {% callout type="danger" %}
 *   <div class="ia-callout ia-callout-tip">   …  </div>     {% callout type="tip" %}
 *   <pre class="ia-code" data-lang="bash">    …  </pre>     ```bash … ```
 *   <a class="ia-link ia-link-external" ...>  …  </a>       external URL
 *
 * Phase 2: server returns `body_ast` (Markdoc Node JSON), FE renders via
 * `@markdoc/markdoc/dist/renderers/react` with the slot tags mapped to
 * Alert / CodeBlock / Tabs etc. — same visual output, native React tree.
 */
export interface InlineHelpBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Pre-compiled HTML served by the DP `/api/v1/inline-docs/{topicId}` endpoint. */
  html: string
  /** Visual density preset. `popover` is compact; `sheet` is full reading width. */
  variant?: 'popover' | 'sheet'
}

export const InlineHelpBody = React.forwardRef<HTMLDivElement, InlineHelpBodyProps>(
  ({ className, html, variant = 'popover', ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'ia-help-prose text-ia-text-secondary',
        variant === 'popover' && 'text-body-sm [&>*+*]:mt-3 leading-relaxed',
        variant === 'sheet' && 'text-body-md [&>*+*]:mt-4 leading-relaxed',
        // Headings
        '[&_h1]:text-heading-lg [&_h1]:text-ia-text [&_h1]:font-semibold',
        '[&_h2]:text-heading-md [&_h2]:text-ia-text [&_h2]:font-semibold [&_h2]:mt-6',
        '[&_h3]:text-heading-sm [&_h3]:text-ia-text [&_h3]:font-semibold [&_h3]:mt-5',
        // Body
        '[&_p]:text-ia-text-secondary',
        '[&_strong]:text-ia-text [&_strong]:font-semibold',
        '[&_em]:italic',
        // Inline code
        '[&_code]:bg-ia-code [&_code]:text-ia-text-code [&_code]:px-1 [&_code]:py-[1px]',
        '[&_code]:rounded-sm [&_code]:font-mono [&_code]:text-[0.92em]',
        // Lists
        '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
        '[&_li]:my-1',
        // Links (Markdoc {% link %} or plain markdown)
        '[&_a]:text-ia-green [&_a]:font-medium hover:[&_a]:underline',
        '[&_a.ia-link-external]:after:content-["_↗"]',
        // Callouts — match Alert visuals via Tailwind utility composition.
        // Same border/bg recipe as alertVariants in atoms/Alert.tsx so the
        // server-emitted callouts read as the same component to the eye.
        '[&_.ia-callout]:flex [&_.ia-callout]:gap-3 [&_.ia-callout]:items-start',
        '[&_.ia-callout]:rounded-md [&_.ia-callout]:border [&_.ia-callout]:px-4 [&_.ia-callout]:py-3',
        '[&_.ia-callout-info]:bg-ia-info-bg [&_.ia-callout-info]:border-ia-info/20',
        '[&_.ia-callout-warn]:bg-ia-warning-bg [&_.ia-callout-warn]:border-ia-warning/20',
        '[&_.ia-callout-danger]:bg-ia-error-bg [&_.ia-callout-danger]:border-ia-error/20',
        '[&_.ia-callout-tip]:bg-ia-green-muted [&_.ia-callout-tip]:border-ia-green/20',
        // Fenced code (server emits <pre class="ia-code" data-lang>)
        '[&_pre.ia-code]:bg-ia-code [&_pre.ia-code]:border [&_pre.ia-code]:border-ia-border',
        '[&_pre.ia-code]:rounded-md [&_pre.ia-code]:p-4 [&_pre.ia-code]:overflow-x-auto',
        '[&_pre.ia-code]:font-mono [&_pre.ia-code]:text-code [&_pre.ia-code]:text-ia-text-code',
        // Divider
        '[&_hr]:my-6 [&_hr]:border-t [&_hr]:border-ia-border',
        className,
      )}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: server-compiled, safelisted Markdoc HTML
      dangerouslySetInnerHTML={{ __html: html }}
      {...props}
    />
  ),
)
InlineHelpBody.displayName = 'InlineHelpBody'
